import type { SessionSimBreakdown } from '../api/sessions';

/**
 * Side-by-side per-SIM summary for a session.
 *
 * On a dual-SIM drive both slots travel the same route at the same timestamps,
 * so the columns are directly comparable — that is the whole point of recording
 * them separately rather than folding everything into the serving cell.
 */
export default function SessionSimBreakdownPanel({ sims }: { sims: SessionSimBreakdown[] }) {
  // Single-SIM drives and pre-per-SIM sessions have nothing to compare.
  if (sims.length < 2) return null;

  const best = (pick: (s: SessionSimBreakdown) => number | null) => {
    const vals = sims.map(pick).filter((v): v is number => v != null);
    return vals.length ? Math.max(...vals) : null;
  };
  const bestRsrp = best((s) => s.avgRsrp);
  const bestSinr = best((s) => s.avgSinr);

  return (
    <div className="border-t border-slate-800 bg-slate-950 px-4 py-3">
      <div className="text-slate-400 text-xs font-semibold uppercase tracking-wide mb-2">
        Per-SIM comparison
      </div>
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${sims.length}, minmax(0, 1fr))` }}>
        {sims.map((s) => {
          const gens = s.generationCounts ?? {};
          const totalGen = Object.values(gens).reduce((a, b) => a + b, 0);
          return (
            <div
              key={s.slotIndex}
              className={`rounded-md border p-3 ${
                s.wasDataSim ? 'border-blue-600 bg-slate-900' : 'border-slate-800 bg-slate-900/50'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-slate-200 text-sm font-semibold">
                  SIM {s.slotIndex + 1}
                </span>
                {/* Throughput, latency and MOS on this session were measured
                    over this slot only. */}
                {s.wasDataSim && (
                  <span className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                    DATA
                  </span>
                )}
              </div>
              <div className="text-slate-400 text-xs mb-3">
                {s.carrierName ?? '—'}
                {s.mcc && s.mnc ? ` · ${s.mcc}-${s.mnc}` : ''}
              </div>

              <dl className="grid grid-cols-2 gap-y-2 gap-x-3 text-xs">
                <Stat label="Points" value={s.pointCount.toLocaleString()} />
                <Stat label="Cells" value={String(s.uniqueCells)} />
                <Stat
                  label="Avg RSRP"
                  value={s.avgRsrp != null ? `${s.avgRsrp} dBm` : '—'}
                  highlight={s.avgRsrp != null && s.avgRsrp === bestRsrp}
                />
                <Stat
                  label="Avg SINR"
                  value={s.avgSinr != null ? `${s.avgSinr} dB` : '—'}
                  highlight={s.avgSinr != null && s.avgSinr === bestSinr}
                />
                <Stat
                  label="RSRP range"
                  value={s.minRsrp != null && s.maxRsrp != null ? `${s.minRsrp} / ${s.maxRsrp}` : '—'}
                />
                <Stat label="Avg RSRQ" value={s.avgRsrq != null ? `${s.avgRsrq} dB` : '—'} />
              </dl>

              {totalGen > 0 && (
                <div className="mt-3">
                  <div className="text-slate-500 text-[10px] uppercase tracking-wide mb-1">
                    Coverage mix
                  </div>
                  <div className="flex h-2 rounded overflow-hidden bg-slate-800">
                    {Object.entries(gens)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([gen, n]) => (
                        <div
                          key={gen}
                          className={genColor(gen)}
                          style={{ width: `${(n / totalGen) * 100}%` }}
                          title={`${gen}: ${((n / totalGen) * 100).toFixed(1)}%`}
                        />
                      ))}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[10px] text-slate-400">
                    {Object.entries(gens)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([gen, n]) => (
                        <span key={gen}>
                          {gen} {((n / totalGen) * 100).toFixed(0)}%
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <dt className="text-slate-500 text-[10px] uppercase tracking-wide">{label}</dt>
      <dd className={`font-mono ${highlight ? 'text-emerald-400 font-semibold' : 'text-slate-200'}`}>
        {value}
      </dd>
    </div>
  );
}

function genColor(gen: string): string {
  const g = gen.toUpperCase();
  if (g.includes('5G')) return 'bg-emerald-500';
  if (g.includes('4G') || g.includes('LTE')) return 'bg-blue-500';
  if (g.includes('3G')) return 'bg-amber-500';
  if (g.includes('2G')) return 'bg-rose-500';
  return 'bg-slate-600';
}
